import { useState, useEffect } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { Download, RefreshCw, X, Share2, Smartphone } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

export function PwaPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showInstallBanner, setShowInstallBanner] = useState(false)
  const [showIosGuide, setShowIosGuide] = useState(false)
  const [isIos, setIsIos] = useState(false)
  const [isStandalone, setIsStandalone] = useState(false)

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, r) {
      if (r) {
        // Cek update berkala setiap 60 menit
        setInterval(() => {
          r.update().catch(() => {})
        }, 60 * 60 * 1000)
      }
    },
    onRegisterError(error) {
      console.warn('PWA ServiceWorker registration failed:', error)
    },
  })

  useEffect(() => {
    // Cek apakah sudah running dalam mode standalone (PWA terinstall)
    const standaloneMode =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true
    setIsStandalone(Boolean(standaloneMode))

    // Deteksi iOS device
    const userAgent = window.navigator.userAgent.toLowerCase()
    const iosDevice = /iphone|ipad|ipod/.test(userAgent)
    setIsIos(iosDevice)

    // Tangkap event instalasi browser (Android Chrome, Edge, Desktop Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      const dismissed = sessionStorage.getItem('wicend_pwa_dismissed') === 'true'
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      if (!dismissed) {
        setShowInstallBanner(true)
      }
    }

    // Tangkap saat user berhasil install
    const handleAppInstalled = () => {
      setShowInstallBanner(false)
      setDeferredPrompt(null)
      sessionStorage.removeItem('wicend_pwa_dismissed')
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      if (isIos) {
        setShowIosGuide(true)
      }
      return
    }

    await deferredPrompt.prompt()
    const choiceResult = await deferredPrompt.userChoice
    if (choiceResult.outcome === 'accepted') {
      setShowInstallBanner(false)
    }
    setDeferredPrompt(null)
  }

  const handleDismissInstall = () => {
    setShowInstallBanner(false)
    sessionStorage.setItem('wicend_pwa_dismissed', 'true')
  }

  return (
    <>
      {/* 1. Prompt Update Aplikasi Tersedia */}
      {needRefresh && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-96 z-50 animate-in fade-in slide-in-from-bottom-5 duration-300">
          <div className="bg-slate-900 text-white rounded-2xl p-4 shadow-2xl border border-slate-700/60 flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-xl bg-teal-500/20 text-teal-400 shrink-0">
                <RefreshCw className="w-5 h-5 animate-spin" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-bold text-white">Versi Baru Tersedia</h4>
                <p className="text-xs text-slate-300 mt-0.5">
                  Pembaruan sistem telah siap. Muat ulang sekarang untuk menerapkan fitur terbaru.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setNeedRefresh(false)}
                className="text-slate-400 hover:text-white p-1 rounded-lg transition-colors"
                aria-label="Tutup"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center justify-end gap-2 pt-1 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setNeedRefresh(false)}
                className="px-3 py-1.5 text-xs font-semibold text-slate-400 hover:text-white transition-colors"
              >
                Nanti
              </button>
              <button
                type="button"
                onClick={() => updateServiceWorker(true)}
                className="px-3.5 py-1.5 text-xs font-bold bg-teal-600 hover:bg-teal-500 text-white rounded-xl shadow-sm transition-colors flex items-center gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Muat Ulang
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Banner Tawaran Pasang PWA (Install Prompt) */}
      {!isStandalone && showInstallBanner && (
        <div className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:w-96 z-40 animate-in fade-in slide-in-from-bottom-5 duration-300">
          <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded-2xl p-4 shadow-xl border border-teal-600/20 dark:border-teal-500/20 flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-xl bg-teal-50 dark:bg-teal-950/60 text-teal-600 dark:text-teal-400 shrink-0">
                <Smartphone className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-bold">Pasang Aplikasi WICEND</h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Tambahkan ke layar utama perangkat Anda untuk akses instan dan pengalaman lebih lancar.
                </p>
              </div>
              <button
                type="button"
                onClick={handleDismissInstall}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-white p-1 rounded-lg transition-colors"
                aria-label="Tutup"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center justify-end gap-2 pt-1 border-t border-slate-100 dark:border-slate-800">
              <button
                type="button"
                onClick={handleDismissInstall}
                className="px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white transition-colors"
              >
                Lain Kali
              </button>
              <button
                type="button"
                onClick={handleInstallClick}
                className="px-3.5 py-1.5 text-xs font-bold bg-teal-700 hover:bg-teal-600 text-white rounded-xl shadow-sm transition-colors flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" />
                Install Sekarang
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3. Panduan Install Khusus iOS Safari Modal */}
      {showIosGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-sm w-full p-5 shadow-2xl border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-teal-600" />
                Pasang di iPhone / iPad
              </h3>
              <button
                type="button"
                onClick={() => setShowIosGuide(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
              Untuk menginstal aplikasi ini di perangkat Apple (iOS), ikuti 2 langkah mudah berikut:
            </p>
            <ol className="text-xs space-y-2.5 text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/60 p-3.5 rounded-xl border border-slate-100 dark:border-slate-800">
              <li className="flex items-start gap-2">
                <span className="font-bold text-teal-600 shrink-0">1.</span>
                <span>
                  Ketuk tombol <strong className="inline-flex items-center gap-1 font-semibold text-slate-900 dark:text-white"><Share2 className="w-3.5 h-3.5 text-blue-500" /> Bagikan (Share)</strong> pada bilah bawah browser Safari.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-teal-600 shrink-0">2.</span>
                <span>
                  Gulir ke bawah lalu pilih menu <strong className="font-semibold text-slate-900 dark:text-white">"Tambahkan ke Layar Utama" (Add to Home Screen)</strong>.
                </span>
              </li>
            </ol>
            <button
              type="button"
              onClick={() => setShowIosGuide(false)}
              className="w-full py-2 text-xs font-bold bg-teal-700 hover:bg-teal-600 text-white rounded-xl transition-colors"
            >
              Saya Mengerti
            </button>
          </div>
        </div>
      )}
    </>
  )
}
